import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FiUser, FiLock } from 'react-icons/fi';
import { useGoogleLogin } from '@react-oauth/google';
import liwazaLogo from '../assets/liwaza-logo.png';
import './Authentication.css';
import googleLogo from '../assets/google-logo.png';
import {
  API_BASE_URL,
  GOOGLE_CLIENT_ID,
  consumeStoredLoginRedirectMessage,
  consumeStoredPostLoginRedirect,
  toastdisplay,
  storeUserSession,
  detectDevice,
  redirectBasedOnPremiumStatus,
} from '../utils/utils';
import { decodeCoachId } from '../utils/hashids';
import {
  normalizeCountryCode,
  normalizeDisplayCurrency,
  subscriptionAmounts,
} from '../pricing/pricingStripeConfig';
import { claimWebinarRegistrationIfPending } from '../core/webinars/webinarPublicApi';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getTurnstileToken } from '../utils/turnstile';
import { getPaymentIntentFromStorageOrSearchParams } from '../utils/paymentRedirectIntent';
import { redirectToStaffConsole } from '../utils/staffAppAccess';
import PhoneInput, { isPhoneNumberComplete } from '../components/PhoneInput/PhoneInput';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const coach_id = (() => {
    const coachIdParam = searchParams.get('coach_id');
    if (!coachIdParam) return null;
    try {
      return decodeCoachId(coachIdParam);
    } catch (error) {
      console.error('Invalid coach_id hash:', error);
      return null;
    }
  })();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    whatsappNumber: '',
    password: '',
    confirmPassword: '',
    avatarName: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  const readStoredJson = (key) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error(`Error parsing ${key}:`, error);
      return null;
    }
  };

  const readSessionUser = () => {
    try {
      const raw = sessionStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Error parsing session user:', error);
      return null;
    }
  };

  // Show message from navigation state (e.g., when redirected from a post link)
  React.useEffect(() => {
    const redirectMessage =
      location.state?.message || consumeStoredLoginRedirectMessage();
    if (redirectMessage) {
      toastdisplay(redirectMessage, false, 4000, setToast);
    }
  }, [location.state]);

  const generateAvatarName = (firstName, lastName) => {
    const getFirstThree = (str) =>
      str.length >= 3 ? str.substring(0, 3) : str;
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${getFirstThree(firstName)}${getFirstThree(lastName)}${randomNum}`.toLowerCase();
  };

  const parseApiResponse = async (response) => {
    const raw = await response.text();
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  };

  const getSignupSuccessToastConfig = (isMobile, paymentIntent) => {
    const baseMessage = paymentIntent
      ? 'Account created! Proceeding to payment...'
      : 'Signup successful. Redirecting...';

    if (!isMobile) {
      return { message: baseMessage, duration: 2000 };
    }

    return {
      message: `${baseMessage} For the best Liwaza experience, we recommend using a desktop, laptop, or tablet. Some features may be limited on mobile devices.`,
      duration: 5000,
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...form, [name]: value };

    if (name === 'firstName' || name === 'lastName') {
      updatedForm.avatarName = generateAvatarName(
        updatedForm.firstName,
        updatedForm.lastName
      );
    }

    setForm(updatedForm);
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors, [name]: '' };

      if (name === 'password' || name === 'confirmPassword') {
        if (!updatedForm.confirmPassword.trim()) {
          nextErrors.confirmPassword = '';
        } else if (updatedForm.password !== updatedForm.confirmPassword) {
          nextErrors.confirmPassword = 'Passwords do not match.';
        } else {
          nextErrors.confirmPassword = '';
        }
      }

      return nextErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required.';
    if (!form.email.trim() || !emailRegex.test(form.email))
      newErrors.email = 'Please enter a valid email address.';
    if (!form.whatsappNumber.trim())
      newErrors.whatsappNumber = 'WhatsApp number is required.';
    else if (!isPhoneNumberComplete(form.whatsappNumber))
      newErrors.whatsappNumber = 'Please enter a valid WhatsApp number.';
    if (!form.password.trim() || form.password.length < 8)
      newErrors.password = 'Password should be at least 8 characters.';
    if (!form.confirmPassword.trim())
      newErrors.confirmPassword = 'Please confirm your password.';
    else if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const captcha_token = await getTurnstileToken();
      const response = await fetch(`${API_BASE_URL}/api/signup/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          phone_number: form.whatsappNumber.trim(),
          password: form.password,
          confirm_password: form.confirmPassword,
          avatar_name: form.avatarName,
          referral_coach_id: coach_id,
          captcha_token,
        }),
      });

      const data = await parseApiResponse(response);

      if (!response.ok) {
        toastdisplay(
          getErrorMessage(data?.detail ?? data, 'Signup failed. Please try again.'),
          false,
          3000,
          setToast
        );
        setIsLoading(false);
        return; // Stop execution to prevent accessing undefined properties
      }

      if (!data || typeof data !== 'object' || !data.user) {
        throw new Error('Signup failed. Please try again.');
      }

      await storeUserSession(data.user, data.token);
      await claimWebinarRegistrationIfPending();

      // Keep a single post-signup toast so mobile users do not see overlapping messages.
      const deviceType = detectDevice();
      const isMobile = deviceType === 'mobile';

      const paymentIntent = localStorage.getItem('payment_intent');
      const successToast = getSignupSuccessToastConfig(isMobile, paymentIntent);
      toastdisplay(successToast.message, true, successToast.duration, setToast);

      const navigationDelay = successToast.duration;
      setTimeout(() => handleRedirectAfterSignup(), navigationDelay);
    } catch (err) {
      toastdisplay(
        getErrorMessage(err, 'Signup failed. Please try again.'),
        false,
        3000,
        setToast
      );
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async (credentialResponse) => {
    console.log('Google credential response:', credentialResponse);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/google-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: credentialResponse.access_token,
          referral_coach_id: coach_id,
        }),
      });

      const data = await parseApiResponse(response);

      if (!response.ok) {
        const detail = getErrorMessage(data?.detail ?? data, 'Google signup failed.');
        toastdisplay(detail, false, detail.includes('/admin') ? 6000 : 3000, setToast);
        if (response.status === 403 && String(detail).includes('/admin')) {
          setTimeout(() => redirectToStaffConsole(), 2500);
        }
        setIsLoading(false);
        return;
      }

      if (!data || typeof data !== 'object' || !data.user) {
        throw new Error('Google signup failed.');
      }

      await storeUserSession(data.user, data.token);
      await claimWebinarRegistrationIfPending();

      const deviceType = detectDevice();
      const isMobile = deviceType === 'mobile';

      const paymentIntent = localStorage.getItem('payment_intent');
      const successToast = getSignupSuccessToastConfig(isMobile, paymentIntent);
      toastdisplay(successToast.message, true, successToast.duration, setToast);

      const navigationDelay = successToast.duration;
      setTimeout(() => handleRedirectAfterSignup(), navigationDelay);
    } catch (err) {
      toastdisplay(
        getErrorMessage(err, 'Google signup failed.'),
        false,
        3000,
        setToast
      );
      setIsLoading(false);
    }
  };

  // Inside your Signup component:
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSignup,
    onError: () => {
      toastdisplay('Google Sign-Up failed', false, 2000, setToast);
      setIsLoading(false);
    },
  });

  const handleRedirectAfterSignup = () => {
    // Check if user was trying to access a specific page before signup (highest priority).
    // Navigate immediately to avoid stacked timers that race against route guards.
    const redirectAfterLogin = consumeStoredPostLoginRedirect();
    if (redirectAfterLogin) {
      toastdisplay(
        'Account created! Redirecting to your requested content...',
        true,
        1500,
        setToast
      );
      navigate(redirectAfterLogin, { replace: true });
      return;
    }

    // Check if there's a specific redirect URL from the navigation state (e.g., direct post link)
    const redirectTo = location.state?.redirectTo;
    if (redirectTo) {
      toastdisplay(
        'Account created! Redirecting to your requested content...',
        true,
        1500,
        setToast
      );
      navigate(redirectTo, { replace: true });
      return;
    }

    const intent = getPaymentIntentFromStorageOrSearchParams(searchParams);

    // Check for payment redirect context from storage or query params
    if (intent?.action === 'subscribe') {
      toastdisplay(
        'Account created! Proceeding to payment...',
        true,
        2000,
        setToast
      );
      setTimeout(() => {
        handlePaymentRedirect(intent);
      }, 2000);
      return;
    }

    // Get user data to check first-time status
    const user = readSessionUser();

    // Check if user came from coach profile
    const fromCoachProfile = localStorage.getItem('fromCoachProfile');
    const pendingCoachAction = localStorage.getItem('pendingCoachAction');

    if (fromCoachProfile === 'true' && pendingCoachAction) {
      // Redirect to pricing page to show both cards
      navigate('/pricing');
    } else if (
      user &&
      user.role?.name !== 'coach' &&
      !user.is_premium &&
      user.is_first_time === false &&
      !user.had_premium_before &&
      user.user_type !== 'premium_lapsed' &&
      !user.is_premium_lapsed
    ) {
      // If user is not premium, not first-time, and never had premium before, redirect to community
      navigate('/community');
    } else {
      // Use regular redirect flow for premium users, first-time users, or users who had premium before
      redirectBasedOnPremiumStatus(navigate, coach_id);
    }
  };

  const handlePaymentRedirect = (intentOverride = null) => {
    const intent =
      intentOverride || getPaymentIntentFromStorageOrSearchParams(searchParams);
    let planKey = 'monthly';
    let billingCurrency = 'eur';
    let productLine = 'graduate_full';

    const user = readSessionUser();

    if (intent) {
      planKey = intent.plan || 'monthly';
      if (planKey === '11months') planKey = '12months';
      billingCurrency =
        (intent.billingCurrency || 'eur').toLowerCase() === 'usd'
          ? 'usd'
          : 'eur';
      if (intent.productLine === 'executive') productLine = 'executive';
      else if (intent.productLine === 'families') productLine = 'families';
      else if (intent.productLine === 'graduate_ielts')
        productLine = 'graduate_ielts';
      else if (intent.productLine === 'graduate_full')
        productLine = 'graduate_full';
      else if (intent.productType === 'executive-mba')
        productLine = 'executive';
      else if (intent.productType === 'families') productLine = 'families';
    }

    if (productLine === 'executive') {
      planKey = planKey === 'complete' ? 'complete' : '3months';
    }

    const displayCurrency = normalizeDisplayCurrency(
      intent?.displayCurrency || intent?.display_currency,
      billingCurrency === 'usd' ? 'USD' : 'EUR'
    );

    let pricingSegment = 'standard';
    if (user?.pricing_segment === 'legacy_graduate') {
      pricingSegment = 'legacy_graduate';
    } else if (intent?.pricing_segment === 'legacy_graduate') {
      pricingSegment = 'legacy_graduate';
    }

    const snap = subscriptionAmounts({
      productLine,
      billingCurrency,
      planKey,
      pricingSegment,
      displayCurrency,
      countryCode: normalizeCountryCode(intent?.country_code),
    });

    if (!user?.email) {
      toastdisplay(
        'Please sign in again before continuing to payment.',
        false,
        3000,
        setToast
      );
      return;
    }

    if (!snap.stripeUrl) {
      toastdisplay(
        'Payment link configuration is missing for this plan. Please contact support.',
        false,
        4000,
        setToast
      );
      return;
    }

    localStorage.setItem(
      'pending_subscription',
      JSON.stringify({
        user_email: user.email,
        timestamp: Date.now(),
        price_eur: snap.amountEur,
        price: snap.displayAmount,
        billing_price: snap.chargeAmount,
        billing_currency: billingCurrency.toUpperCase(),
        currency: snap.displayCurrency,
        display_currency: snap.displayCurrency,
        country_code: intent?.country_code || null,
        currency_symbol: snap.currencySymbol,
        plan: planKey,
        product_line: productLine,
        pricing_segment: pricingSegment,
      })
    );

    const paymentUrl = new URL(snap.stripeUrl);
    paymentUrl.searchParams.set('prefilled_email', user.email);
    localStorage.removeItem('payment_intent');
    window.location.href = paymentUrl.toString();
  };

  return (
    <div className="liwaza-layout">
        <div style={{ width: '100%', maxWidth: 400, marginBottom: 8 }}>
          <img
            src={liwazaLogo}
            alt="Liwaza Logo"
            style={{ height: '32px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </div>

        <form className="liwaza-form" onSubmit={handleSubmit}>
          <h1 className="liwaza-title">Start your journey</h1>
          {/* Google Sign Up */}
          <div className="google-login-column">
            <div className="google-login-column">
              <button
                className="btn-lwz-primary"
                onClick={() => googleLogin()}
                disabled={isLoading}
              >
                <img
                  src={googleLogo}
                  alt="Google"
                  style={{ width: 20, marginRight: 8 }}
                />
                {isLoading ? 'Being signed up...' : 'Register with Google'}
              </button>
            </div>
            <div className="google-login-title">or</div>
          </div>

          {/* First Name */}
          <div className="liwaza-field">
            <label>First name</label>
            <div className={`liwaza-input-wrapper ${errors.firstName ? 'error' : ''}`}>
              <FiUser className="liwaza-icon" />
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="First name"
                aria-invalid={Boolean(errors.firstName)}
                required
              />
            </div>
            {errors.firstName ? (
              <span className="liwaza-field-error">{errors.firstName}</span>
            ) : null}
          </div>

          {/* Last Name */}
          <div className="liwaza-field">
            <label>Last name</label>
            <div className={`liwaza-input-wrapper ${errors.lastName ? 'error' : ''}`}>
              <FiUser className="liwaza-icon" />
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Last name"
                aria-invalid={Boolean(errors.lastName)}
                required
              />
            </div>
            {errors.lastName ? (
              <span className="liwaza-field-error">{errors.lastName}</span>
            ) : null}
          </div>

          {/* Avatar Name */}
          <div className="liwaza-field">
            <label>Avatar Name (auto-generated)</label>
            <div className="liwaza-input-wrapper">
              <input
                type="text"
                name="avatarName"
                value={form.avatarName}
                readOnly
                style={{ backgroundColor: '#f8f1f6', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Email */}
          <div className="liwaza-field">
            <label>Email address</label>
            <div className={`liwaza-input-wrapper ${errors.email ? 'error' : ''}`}>
              <FiUser className="liwaza-icon" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@email.com"
                aria-invalid={Boolean(errors.email)}
                required
              />
            </div>
            {errors.email ? (
              <span className="liwaza-field-error">{errors.email}</span>
            ) : null}
          </div>

          {/* WhatsApp */}
          <div className="liwaza-field">
            <label>WhatsApp number</label>
            <PhoneInput
              value={form.whatsappNumber}
              onChange={(value) => {
                setForm((prev) => ({ ...prev, whatsappNumber: value }));
                setErrors((prevErrors) => ({ ...prevErrors, whatsappNumber: '' }));
              }}
              disabled={isLoading}
              fullWidth
              required
              error={Boolean(errors.whatsappNumber)}
              errorMessage={errors.whatsappNumber}
            />
            <span className="liwaza-field-hint" style={{ marginTop: 6, display: 'block', fontSize: 13, color: '#625b71' }}>
              Used for important updates and reminders via WhatsApp.
            </span>
          </div>

          {/* Password */}
          <div className="liwaza-field">
            <label>Password</label>
            <div className={`liwaza-input-wrapper ${errors.password ? 'error' : ''}`}>
              <FiLock className="liwaza-icon" />
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Create a password"
                aria-invalid={Boolean(errors.password)}
                required
              />
            </div>
            {errors.password ? (
              <span className="liwaza-field-error">{errors.password}</span>
            ) : null}
          </div>

          {/* Confirm Password */}
          <div className="liwaza-field">
            <label>Confirm password</label>
            <div
              className={`liwaza-input-wrapper ${errors.confirmPassword ? 'error' : ''}`}
            >
              <FiLock className="liwaza-icon" />
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                aria-invalid={Boolean(errors.confirmPassword)}
                required
              />
            </div>
            {errors.confirmPassword ? (
              <span className="liwaza-field-error">{errors.confirmPassword}</span>
            ) : null}
          </div>

          <button
            type="submit"
            className="btn-lwz-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Being signed up...' : 'Sign up'}
          </button>
          <button
            type="button"
            className="btn-lwz-outline"
            disabled={isLoading}
            onClick={() => {
              let loginUrl = '/login';
              const params = new URLSearchParams(searchParams);

              if (params.toString()) {
                loginUrl += '?' + params.toString();
              }

              navigate(loginUrl);
            }}
          >
            Back to login
          </button>
        </form>

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <a href="/terms" className="liwaza-legal-link">
            Terms and conditions
          </a>
        </div>

        {toast.show && (
          <div
            className={`toast-notification ${toast.success ? 'success' : 'error'}`}
          >
            <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
  );
};

export default Signup;
